'use client';

import { ButtonGroup, Flex, Icon, Text, Tooltip, useToast } from '@chakra-ui/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import React, { Suspense, useCallback, useState, useMemo, useEffect } from 'react';
import { RiMoreFill } from 'react-icons/ri';
import { FaMapPin } from 'react-icons/fa6';
import { useProfileContext } from 'shared/misc/providers/ProfileContext';
import normalizeUrl from 'normalize-url';
import BasicEditor from 'web2/app/components/by-type/rich-text/basic/basic-editor';
import { gql } from 'shared/__generated__/gql';
import { useAuth } from 'shared/misc/hooks/useAuth';
import IsLoggedInTooltip from 'shared/misc/components/gates/IsLoggedInTooltip';
import { copyToClipboard } from 'shared/misc/clipboard';
import { BASE_SITE_URL } from 'shared/siteconfig';
import { ModalNames, useModalState } from 'shared/misc/wrappers/ModalProvider';
import StarContainer from './-profile-stars';
import PerfImageFromFile from 'shared/misc/components/util/PerfImageFromFile';
import { ProfileMutualFragment, ProfileThemeType, UserState } from 'shared/__generated__/graphql';
import UserTags from 'shared/misc/components/by-type/user/UserTags';
import { normalizeSocialUsername } from 'shared/url';
import useFollowUser from 'shared/misc/hooks/useFollowUser';
import useBlockUserWithToast from 'shared/misc/hooks/useBlockUserWithToast';
import { useMutation, useQuery } from '@apollo/client';
import _ from 'lodash';
import { getEmojisAsArrayFromString } from 'shared/emoji';
import { useRouter } from '@tanstack/react-router';
import { format } from 'date-fns';
import Heading from 'shared/misc/components/heading';
import { CREATE_REPORT_MUTATION } from 'shared/misc/graphql/UserReportMutations';
import { SPOTLIGHT_USER_MUTATION } from 'shared/misc/graphql/CurateMutations';
import { isUserCurator, newConversationsEnabled } from 'shared/gates';
import ProfileMainLowerContent from './-profile-main-lower-content';
import { useConvoCreationHooks } from 'shared/misc/hooks/useConvoHooks';
import { UserOnlineIndicator } from 'web2/app/components/by-type/user/user-online-indicator';
import { useThemeColors } from 'shared/misc/hooks/useThemeColors';
import { Button as TWButton } from '../../../components/button';
import { Link as TWLink } from '../../../components/link';
import EditThemeModal from 'web2/app/components/modals/edit-theme-modal';
import { useDisclosure } from 'web2/app/misc/wrappers/use-disclosure';
import { useAutoSetTheme } from 'shared/misc/providers/ThemeContext';

export default function ProfileMainContent({ loading }: { loading?: boolean }) {
  const { profile } = useProfileContext();
  const theme = profile?.profileTheme;
  const { user } = useAuth();
  const editThemeModalState = useDisclosure();

  const setTheme = useAutoSetTheme(theme || ProfileThemeType.Default, profile?.customTheme?.colors || null, editThemeModalState.isOpen);

  const colors = useThemeColors();

  const {
    username,
    profilePhotoFile,
    location,
    instagram,
    twitter,
    website,
    links,
    recs,
    recCount,
    updatedAt,
    bioLexical,
    promptCount,
    bio,
    followingCount,
  } = profile;

  let { firstName, lastName } = profile;

  const [createReport, createReportState] = useMutation(CREATE_REPORT_MUTATION, {
    variables: { targetId: profile?.id, targetType: 'user', context: 'REPORT' },
  });

  const onCreateReport = useCallback(() => {
    createReport({
      onCompleted: () => {
        toast({
          title: 'Reported user.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      },
    });
  }, [createReport]);

  const addRecommendationModalState = useModalState(ModalNames.ADD_RECOMMENDATION);

  const { followUser, unfollowUser, isSubmitting } = useFollowUser({ user: profile });

  const block = useBlockUserWithToast({ user: profile });

  const toast = useToast();

  const onClickCopyLink = useCallback(() => {
    copyToClipboard(`${BASE_SITE_URL}/u/${profile.username}`);
    toast({
      title: 'Copied link to profile',
      status: 'success',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });
  }, []);

  const onEditTheme = () => {
    editThemeModalState.onToggle();
  };

  const hasLinks = !!website || !!instagram || !!twitter || !!links?.length;
  const linkArray = [
    instagram && {
      url: 'www.instagram.com/'.concat(normalizeSocialUsername(instagram)),
      title: 'Instagram',
    },
    twitter && { url: 'www.x.com/'.concat(normalizeSocialUsername(twitter)), title: 'Twitter' },
    website && { url: website, title: 'Website' },
    ...(links || []),
  ].filter(Boolean);
  const showMoreLinks: boolean = linkArray.length > 3;

  const router = useRouter();

  const { titleBackground, titleBorder } = colors;

  const [spotlightUser] = useMutation(SPOTLIGHT_USER_MUTATION, {
    variables: { userId: profile?.id as string },
    onCompleted: () => {
      toast({
        title: 'User spotlighted!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const onSpotlight = useCallback(() => {
    spotlightUser();
  }, [createReport]);


  const { startConvoWithUsers, ...convoCreationState } = useConvoCreationHooks();

  const contentWidth = { base: '100%', md: '700px', xl: '800px' };

  const noPublicName =
    false && ((!firstName && !lastName) || profile?.state === UserState.EmailOnlySubstackImport);

  if (profile?.state === UserState.EmailOnlySubstackImport) {
    [firstName, lastName] = profile.username.split('_');
  }

  const linkNode = (
    <>
      {!showMoreLinks ? (
        linkArray.map((link, index) => (
          <TWLink
            key={index}
            to={normalizeUrl(link.url)}
            target="_blank"
            variant="stylized"
            className="typography-action-sm text-uppercase text-brand-main"
          >
            {link.title}
          </TWLink>
        ))
      ) : (
        <>
          {linkArray.slice(0, 2).map((link, index) => (
            <TWLink
              key={index}
              to={normalizeUrl(link.url)}
              target="_blank"
              variant="stylized"
              className="typography-action-sm text-uppercase"
            >
              {link.title}
            </TWLink>
          ))}
          {linkArray.length > 2 && (
            <DropdownMenu>
              <DropdownMenuTrigger>
                <TWButton
                  variant="link"
                  onClick={(e) => e.preventDefault()}
                  style={{ marginRight: '2px' }}
                >
                  More Links...
                </TWButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {linkArray.slice(2).map((link, index) => (
                  <DropdownMenuItem
                    key={index}
                    onSelect={() => window.open(normalizeUrl(link.url), '_blank')}
                  >
                    {link.title}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </>
      )}
    </>
  );

  return (
    <>
      <div className="w-full flex justify-center overflow-x-hidden">
        <div className="w-full md:w-[700px] lg:w-[800px] flex flex-col space-y-6 mt-[48px] sm:mt-[48px] mb-0 lg:mb-[500px] px-4 sm:px-6">
          <div className="order-1 order-lg-1 mb-0 lg:mb-[24px]">
            <Heading
              // order="1"
              as="h1"
              variant="XL"
              fitToContainer
              theme={theme}
              themeColors={colors}
              // fontSize={noPublicName ? { base: '24px', sm: '48px' } : undefined}
              className="text-center text-uppercase"
            >
              {noPublicName ? (
                <span className="whitespace-nowrap">@{username}</span>
              ) : (
                <>
                  {firstName && (
                    <>
                      <span className="whitespace-nowrap">{firstName}</span>{' '}
                    </>
                  )}
                  {lastName && (
                    <span
                      // whiteSpace={typeof window === 'undefined' ? 'nowrap' : 'wrap'}
                      // marginTop={{ base: '24px', sm: '24px', lg: '12px' }}
                      className="whitespace-nowrap mt-[12px] sm:mt-[24px] lg:mt-[12px]"
                    >
                      {lastName}
                    </span>
                  )}
                </>
              )}
            </Heading>
          </div>

          <div className="order-3 bg-brand-background !mt-0 pt-6 order-lg-3 w-full mb-0 md:mb-[20px]">
            <div
              className={`grid h-auto lg:h-[${
                (bio?.length || 0) > 200 ? '150px' : '100px'
              }] max-w-full grid-rows-1 lg:grid-cols-2 lg:grid-rows-1 gap-4 text-body`}
            >
              {/* Left column */}
              <div className="w-auto">
                <div className="p-2 w-full h-full">
                  <div className="flex flex-col gap-[8px]">
                    <div className="flex items-center">
                      <div className="flex flex-row gap-[4px]">
                        <UserOnlineIndicator user={profile} />
                        <UserTags user={profile} showFree />
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="flex items-center flex-row gap-[4px]">
                        <TWLink
                          to={'/u/$username'}
                          params={{ username }}
                          variant="stylizedNoDefaultBorder"
                          className="typography-heading-sm"
                        >
                          @{username}
                        </TWLink>
                        {!profile.isBlockedByViewer && (
                          <Tooltip label="Summary of recent recs. Scroll down to read them.">
                            <span className="cursor-pointer text-[12px] md:text-[16px] typography-heading-sm">
                              {profile?.recs
                                ? getEmojisAsArrayFromString(
                                    profile?.recs?.reduce(
                                      (acc, r) => `${acc}${r.emoji?.trim()}`,
                                      '',
                                    ),
                                  )
                                    .slice(0, 5)
                                    .join(' ')
                                : ''}
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    {(hasLinks || location) && (
                      <div className="flex items-center mt-[6px]">
                        <div className="flex items-center flex-row gap-[4px]">
                          {location && (
                            <div className="items-center">
                              <Icon
                                _groupHover={{
                                  color: 'brand.secondary',
                                }}
                                mr="2px"
                                as={FaMapPin}
                                className="text-brand-main fill-brand-main"
                              />
                              <span className="typography-body text-brand-main text-uppercase">
                                {location}
                              </span>
                            </div>
                          )}
                          {linkNode}
                        </div>
                      </div>
                    )}

                    {(profile.bioLexical || profile.bio) && !profile.isBlockedByViewer && (
                      <div className="w-full flex flex-start mt-[6px] max-h-[100px] overflow-y-scroll">
                        <div className="typography-body text-brand-main font-normal">
                          {profile.bioLexical && typeof window !== 'undefined' ? (
                            <BasicEditor
                              key={updatedAt}
                              isReadOnly={true}
                              initialEditorState={bioLexical}
                              noOfLines={4}
                              contentEditableProps={{ className: '!text-brand-main' }}
                              containerProps={{
                                className: 'text-brand-main max-h-[100px] overflow-y-auto',
                              }}
                            />
                          ) : (
                            <span className="typography-body text-brand-main font-normal">
                              {bio}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right column */}
              <div className="w-auto">
                <div className="p-2 space-y-1 w-full">
                  {profile.relationshipStatus && (
                    <div className="flex justify-between text-brand-main">
                      <span className="typography-action-sm">Relationship Status</span>
                      <span className="typography-action-sm">{profile.relationshipStatus}</span>
                    </div>
                  )}
                  {profile.interestedIn && (
                    <div className="flex justify-between text-brand-main">
                      <span className="typography-action-sm">Interested In</span>
                      <span className="typography-action-sm">{profile.interestedIn}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-brand-main">
                    <span className="typography-action-sm">Joined</span>
                    <span className="typography-action-sm">
                      {format(new Date(profile.createdAt), 'MMMM d, yyyy')}
                    </span>
                  </div>
                  <div className="flex justify-between text-brand-main">
                    <span className="typography-action-sm">Recs</span>
                    <span className="typography-action-sm">
                      <TWLink
                        to={`/u/${profile.username}/feed?types=ADD_RECOMMENDATION`}
                        params={{ username: profile.username }}
                        variant="stylized"
                      >
                        {recCount || 0} {recCount !== 1 ? 'Recs' : 'Rec'}
                      </TWLink>
                    </span>
                  </div>
                  <div className="flex justify-between pb-[12px] text-brand-main">
                    <span className="typography-action-sm">Following</span>
                    <span>
                      <TWLink
                        to={'/u/$username/following'}
                        params={{ username: profile.username }}
                        variant="stylized"
                        className="typography-action-sm"
                      >
                        {followingCount || 0} {followingCount !== 1 ? 'PEOPLE' : 'PERSON'}
                      </TWLink>
                    </span>
                  </div>

                  <ButtonGroup
                    mt="12px"
                    spacing="8px"
                    textStyle="brand.actionSm"
                    fontSize="13.5px"
                    display="flex"
                    justifyContent={{ base: 'stretch', sm: 'flex-end' }}
                    width="100%"
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger className="cursor-pointer">
                        <RiMoreFill className="text-brand-main" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {!!user && profile.id !== user.id && (
                          <DropdownMenuItem
                            disabled={block.isSubmitting}
                            onSelect={(e) => {
                              e.preventDefault();
                              if (profile.isBlockedByViewer) {
                                block.unblockUser();
                              } else {
                                block.blockUser({
                                  onCompleted: () => {
                                    router.replace('/');
                                  },
                                });
                              }
                            }}
                          >
                            {profile.isBlockedByViewer ? 'Unblock' : 'Block'}
                          </DropdownMenuItem>
                        )}
                        {user && isUserCurator(user) && (
                          <DropdownMenuItem
                            onSelect={(e) => {
                              e.preventDefault();
                              onSpotlight();
                            }}
                          >
                            Spotlight user (Curator)
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onSelect={(e) => {
                            e.preventDefault();
                            onCreateReport();
                          }}
                          disabled={createReportState.loading}
                        >
                          Report
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {user?.id !== profile.id && newConversationsEnabled(user) ? (
                      <TWButton
                        variant="secondary"
                        className="flex-auto w-[50%] min-w-[150px] self-stretch typography-action-sm"
                        disabled={convoCreationState.isSubmitting}
                        onClick={async () => {
                          const id = await startConvoWithUsers({ userIds: [profile.id] });
                          if (id) router.navigate({ to: `/convo/${id}` });
                        }}
                      >
                        {convoCreationState.isSubmitting ? 'Sending...' : 'Send Message'}
                      </TWButton>
                    ) : (
                      <div className="flex flex-col gap-[12px] flex-1">
                        <div className="flex flex-row gap-[12px]">
                          <TWButton
                            variant="secondary"
                            className="flex-auto w-[50%] min-w-[150px] self-stretch"
                            onClick={() => {
                              router.navigate({
                                to: '/settings',
                              });
                            }}
                          >
                            Settings
                          </TWButton>
                          <TWButton
                            variant="secondary"
                            className="flex-auto w-[50%] min-w-[150px] self-stretch"
                            onClick={onEditTheme}
                          >
                            Edit Theme
                          </TWButton>
                        </div>
                        <TWButton
                          variant="primary"
                          className="flex-auto w-full min-w-[150px]"
                          onClick={onClickCopyLink}
                        >
                          Share
                        </TWButton>
                      </div>
                    )}

                    {profile.isBlockedByViewer && (
                      <TWButton
                        variant="secondary"
                        className="typography-action-sm text-uppercase flex-auto w-[50%] min-w-[150px] self-stretch"
                        onClick={() => block.unblockUser()}
                        disabled={block.isSubmitting}
                        isLoading={block.isSubmitting}
                      >
                        Unblock user
                      </TWButton>
                    )}
                    {!profile.isBlockedByViewer && (
                      <>
                        {!!user && user?.id !== profile.id ? (
                          <>
                            {profile.amIFollowing ? (
                              <TWButton
                                variant="secondary"
                                className="flex-auto w-[50%] md:w-[40%] xl:min-w-[150px] self-stretch"
                                onClick={() => unfollowUser()}
                                isLoading={
                                  user &&
                                  ((loading && !('amIFollowing' in profile)) || isSubmitting)
                                }
                                isDisabled={
                                  user &&
                                  ((loading && !('amIFollowing' in profile)) || isSubmitting)
                                }
                              >
                                Unfollow
                              </TWButton>
                            ) : (
                              <IsLoggedInTooltip>
                                <TWButton
                                  variant="primary"
                                  className="flex-auto w-[50%] md:w-[50%] xl:min-w-[150px] self-stretch"
                                  isLoading={
                                    user &&
                                    ((loading && !('areTheyFollowing' in profile)) || isSubmitting)
                                  }
                                  isDisabled={
                                    user &&
                                    ((loading && !('areTheyFollowing' in profile)) || isSubmitting)
                                  }
                                  onClick={() =>
                                    user ? followUser() : router.navigate({ pathname: '/sign-in' })
                                  }
                                >
                                  {profile.areTheyFollowing ? 'Follow back' : 'Follow'}
                                </TWButton>
                              </IsLoggedInTooltip>
                            )}
                          </>
                        ) : (
                          (!user || user?.id !== profile.id) && (
                            <IsLoggedInTooltip>
                              <TWButton
                                variant="primary"
                                className="flex-auto w-[50%] md:w-[50%] xl:min-w-[150px] self-stretch"
                                isLoading={!!user && (loading || isSubmitting)}
                                isDisabled={!!user && (loading || isSubmitting)}
                                onClick={() =>
                                  user ? followUser() : router.navigate({ pathname: '/sign-in' })
                                }
                              >
                                {profile.areTheyFollowing ? 'Follow back' : 'Follow'}
                              </TWButton>
                            </IsLoggedInTooltip>
                          )
                        )}
                      </>
                    )}
                  </ButtonGroup>

                  {user && user?.id !== profile.id && !profile.isBlockedByViewer && (
                    <Suspense fallback={null}>
                      <Flex justifyContent={{ base: 'center', md: 'flex-end' }} mt="12px">
                        <RelatedFollowers />
                      </Flex>
                    </Suspense>
                  )}
                </div>
              </div>
            </div>
          </div>
          {profilePhotoFile ? (
            <StarContainer className="order-2 order-lg-2">
              <PerfImageFromFile
                priority={true}
                quality={70}
                file={profilePhotoFile}
                alt="profile photo"
                style={{
                  width: '100vw',
                  height: 'auto',
                }}
              />
            </StarContainer>
          ) : null}
          {!profile.isBlockedByViewer && (
            <Flex
              order="5"
              alignItems="center"
              mt={{ base: '0px', lg: '24px' }}
              className="order-5"
            >
              <ProfileMainLowerContent key={profile.username} />
            </Flex>
          )}
        </div>
      </div>
      {editThemeModalState.isOpen && (
        <EditThemeModal modalState={editThemeModalState} setTheme={setTheme} />
      )}
    </>
  );
}

gql(/* GraphQL */ `
  fragment ProfileMutuals on User {
    id
    username
    relatedFollowersConnection(first: 100) {
      edges {
        node {
          ...ProfileMutual
        }
      }
    }
  }

  fragment ProfileMutual on User {
    id
    username
  }
`);

const ProfileMutualsQuery = gql(/* GraphQL */ `
  query getProfileMutuals($username: String!) {
    user(username: $username) {
      ...ProfileMutuals
    }
  }
`);

function RelatedFollowers({ displayCount = 1 }: { displayCount?: number }) {
  const { profile } = useProfileContext();

  const { data, loading } = useQuery(ProfileMutualsQuery, {
    variables: { username: profile.username },
    fetchPolicy: 'cache-and-network',
  });

  const user = data?.user;

  // if (loading && !user) {
  //   return <PerfSuspense />;
  // }

  const mutualEdges = data?.user?.relatedFollowersConnection.edges || [];

  if (mutualEdges.length === 0 || displayCount === 0) {
    return null;
  }

  if (mutualEdges.length === 1 || displayCount === 1) {
    const mutual = mutualEdges[0].node;
    const remaining = mutualEdges.length - displayCount;

    return (
      <span className="text-right text-brand-main w-full">
        Followed by <TruncateUserLink user={mutual} />
        {displayCount === 1 && remaining !== 0 ? (
          <>
            {' '}
            and{' '}
            <TWLink
              to={'/u/$username/followers'}
              params={{ username: user.username }}
              variant="stylized"
            >
              {remaining} {remaining === 1 ? 'USER' : 'USERS'}
            </TWLink>{' '}
            you know.
          </>
        ) : null}{' '}
      </span>
    );
  }

  if (mutualEdges.length === 2 || displayCount === 2) {
    const mutualA = mutualEdges[0].node;
    const mutualB = mutualEdges[1].node;

    return (
      <Text width="fit-content">
        <TruncateUserLink user={mutualA} /> and <TruncateUserLink user={mutualB} />
      </Text>
    );
  }

  if (mutualEdges.length > 2) {
    const mutualA = mutualEdges[0].node;
    const mutualB = mutualEdges[1].node;
    const remaining = mutualEdges.length - 2;

    return (
      <Text width="fit-content">
        <TruncateUserLink user={mutualA} />, <TruncateUserLink user={mutualB} />, and {remaining}{' '}
        <TWLink
          to={'/u/$username/followers'}
          params={{ username: user.username }}
          variant="stylized"
        >
          other{remaining > 1 ? 's' : ''}
        </TWLink>
      </Text>
    );
  }

  return <Text>{mutualEdges.length} mutuals.</Text>;
}

function TruncateUserLink({ user }: { user: ProfileMutualFragment }) {
  return (
    <TWLink className="link-stylized" to={`/u/${user.username}`}>
      @{_.truncate(user.username, { length: 10 })}
    </TWLink>
  );
}
